import './masterSidebar-v1-1.scss';

import React, { RefObject, useEffect, useRef } from 'react';
import { Drawer } from 'antd';
import { useNarrowLayout } from '../hooks';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { NavLink } from './link';
import { BurgerSVGIcon, CliprLogoSVGIcon, MyUploadsSVGIcon, SidebarBookmarkSVGIcon, SideBarPlusSVGIcon, SearchSVGIcon, AnalyticsSVGIcon } from '../svg';

import { TrainerLinkSVGIcon, UsersLinkSVGIcon, AdminTeamsLinkSVGIcon, CompaniesSVGIcon } from '../svg/svg';
import Routes, { BackRoutes } from '../../routes';
import { useStore } from '../../store/storeHooks';
import { MasterSidebarState } from './masterSidebarState';
import { UserProfile } from '../../entities';
import { DotSpinner } from '../loader';
import { TeamAvatar } from '../teams';

import { SearchTextInputControlled } from '../input';
import { Tooltip } from '../overlays';
import { CreateFirstLibraryGuidedTraining } from './createFirstLibraryGuidedTraining';

type Props = {
  /** Alternative state to be used instead of the default one from Store.pageSidebar. */
  model?: MasterSidebarState;
  user?: UserProfile;
};

const useScrollToTeam = (
  model: MasterSidebarState,
  teamList: RefObject<HTMLUListElement | null>
) => {
  useEffect(() => {
    const hasCurrentTeamLoaded =
      model.currentTeamId && !model.isTeamListLoading && model.teams.length > 0;

    if (hasCurrentTeamLoaded && teamList.current) {
      for (const item of teamList.current.childNodes) {
        const li = item as HTMLElement;

        if (li.dataset.teamId === model.currentTeamId) {
          const container = teamList.current.parentElement || teamList.current;
          // container.scrollTop = li.offsetTop;

          const scrollElementTop = container.getBoundingClientRect().top;   //top value of the scroll element relative to the viewport
          const scrollElementScrollTop = container.scrollTop;               //how much is the element scrolled (always positive or 0)
          const domMomentTop = li.getBoundingClientRect().top;

          const topValue =
            (domMomentTop - scrollElementTop) // determine the position of the scroll item relative to the top of the scroll element
            + scrollElementScrollTop          // add it to the already scrolled value

          container.scrollTo({
            top: topValue,
            // removed the smooth scrolling because the sidebar rerenders each route change
            // causing the container to "lazy" scroll to the previously selected team
            // behavior: 'auto'
          })

          break;
        }
      }
    }
  }, [
    model.currentTeamId,
    model.isTeamListLoading,
    model.teams.length,
    teamList,
  ]);
};

export const MasterSidebar = observer((props: Props) => {
  const store = useStore();
  const narrow = useNarrowLayout();
  const mobileTrigger = useRef<HTMLButtonElement>(null);
  const wasMobileOpen = useRef(false);
  const { pathname } = useLocation();

  const model = props.model || store?.pageSidebar || null;

  const showUsersBtn = store.user?.hasPermission('ViewAdminUsersPage');
  const showTrainerBtn = store.user?.hasPermission('ViewTrainerDashboard');
  const showAdminTeamsBtn = store.user?.hasPermission('AdminGetTeams');
  const showAdminAgenciesBtn = store.user?.hasPermission('AdminManageAgencies');
  const showSettingsBtn = store.user?.hasPermission('ViewSettingsPage');
  const showMyAnalyticsBtn = store.user?.hasPermission('ViewAnalytics');
  const showAgencyDashboardBtn = store.user?.isAgencySponsor;

  model.toggleModel.handleChange = (evt, value) => {
    if (value) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', '');
    }
    model.toggleModel.value = value as string | null;
  };

  useEffect(() => {
    model.mounted();
    return () => model.unmounted();
  }, [model]);

  useEffect(() => { model.setMobileOpen(false); }, [model, pathname, narrow]);
  useEffect(() => {
    if (wasMobileOpen.current && !model.isMobileOpen) mobileTrigger.current?.focus();
    wasMobileOpen.current = model.isMobileOpen;
  }, [model.isMobileOpen]);

  const expanded = narrow || model.isExpanded;
  const sidebarToggleLabel = expanded ? 'Close Sidebar' : 'Open Sidebar';

  const sidebar = (
    <aside
      className="master-sidebar"
      aria-expanded={expanded}
      onClickCapture={narrow ? evt => {
        if ((evt.target as HTMLElement).closest('a[href]')) model.setMobileOpen(false);
      } : undefined}>
      <header className="sidebar-header">
        <div className="sidebar-logo">
          <div className="clipr-logo">
            {expanded && (
              <div>
                <CliprLogoSVGIcon />
              </div>
            )}
          </div>
        </div>
        <Tooltip content={sidebarToggleLabel}>
          <button
            className="expand-btn"
            aria-label={sidebarToggleLabel}
            onClick={narrow ? () => model.setMobileOpen(false) : model.handleCollapseButtonClick}>
            <BurgerSVGIcon />
          </button>
        </Tooltip>
      </header>
      <nav className="sidebar-nav">
        <div className="sidebar-primary-scrollable">
          <ul
            className="sidebar-nav-list"
            aria-expanded={expanded}>
            {showUsersBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.userDashboard()}
                  className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                  aria-current="page">
                  <span className="icon">
                    <UsersLinkSVGIcon />
                  </span>
                  <span className="text">Users</span>
                </NavLink>
              </li>
            )}

            {showTrainerBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.trainerDashboard()}
                  className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                  aria-current="page">
                  <span className="icon">
                    <TrainerLinkSVGIcon />
                  </span>
                  <span className="text">Trainer</span>
                </NavLink>
              </li>
            )}

            {showAgencyDashboardBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.agencyDashboard()}
                  className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                  aria-current="page">
                  <span className="icon">
                    <UsersLinkSVGIcon />
                  </span>
                  <span className="text">Agency</span>
                </NavLink>
              </li>
            )}

            {showAdminTeamsBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.adminTeamsDashboard()}
                  className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                  aria-current="page">
                  <span className="icon">
                    <AdminTeamsLinkSVGIcon />
                  </span>
                  <span className="text">Libraries Admin</span>
                </NavLink>
              </li>
            )}

            {showAdminAgenciesBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.adminAgencies()}
                  className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                  aria-current="page">
                  <span className="icon">
                    <CompaniesSVGIcon />
                  </span>
                  <span className="text">Agencies Admin</span>
                </NavLink>
              </li>
            )}

            <li data-navitem="myUploads" className="sidebar-nav-item">
              <NavLink
                className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                aria-current="page"
                aria-expanded={expanded}
                to={Routes.userDashboard()}>
                <span className="myclipr icon">
                  <MyUploadsSVGIcon />
                </span>
                <span className="text">My Uploads</span>
              </NavLink>
            </li>

            <li className="sidebar-nav-item">
              <NavLink
                className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                aria-current="page"
                aria-expanded={expanded}
                to={Routes.bookmarks()}>
                <span className="icon">
                  <SidebarBookmarkSVGIcon />
                </span>
                <span className="text">My bookmarks</span>
              </NavLink>
            </li>

            {showMyAnalyticsBtn && (<li className="sidebar-nav-item">
              <NavLink
                className={({ isActive }) => `btn btn-core sidepanel-sidebar${isActive ? ' selected' : ''}`}
                aria-current="page"
                aria-expanded={expanded}
                to={Routes.analytics()}>
                <span className="icon">
                  <AnalyticsSVGIcon />
                </span>
                <span className="text">My Analytics</span>
              </NavLink>
            </li>
          )}

            {showSettingsBtn && (
              <li className="sidebar-nav-item">
                <NavLink
                  to={Routes.account}
                  className="btn btn-core sidepanel-sidebar"
                  aria-current="page">
                  <span className="text">Settings</span>
                </NavLink>
              </li>
            )}
          </ul>
        </div>
        <ul
          className="sidebar-library-list"
          aria-expanded={expanded}>
          <li className="divider" />

          <MasterSidebarTeamList model={model} expanded={expanded} />
        </ul>
      </nav>

      <CreateFirstLibraryGuidedTraining model={store.createFirstLibraryGuidedTraining} />
    </aside>
  );

  if (!narrow) return sidebar;
  return (
    <>
      <button ref={mobileTrigger} className="mobile-navigation-trigger" aria-label="Open navigation"
        aria-haspopup="dialog" aria-expanded={model.isMobileOpen}
        onClick={() => model.setMobileOpen(true)}><BurgerSVGIcon /></button>
      <Drawer title="Navigation" placement="left" open={model.isMobileOpen}
        focusable={{ focusTriggerAfterClose: false }}
        onClose={() => model.setMobileOpen(false)} size="min(22rem, calc(100vw - 32px))"
        rootClassName="mobile-navigation-drawer" destroyOnHidden>
        {sidebar}
      </Drawer>
    </>
  );
});

const MasterSidebarTeamList = observer(
  ({ model, expanded }: { model: MasterSidebarState; expanded: boolean }) => {
    const showCreateTeamBtn = model.store.user?.hasPermission('CreateTeam');
    const { pathname } = useLocation();
    const createTeamLink = BackRoutes[Routes.createTeam()].route({
      currentPath: pathname,
    });

    const teamList = useRef<HTMLUListElement>(null);

    useScrollToTeam(model, teamList);

    return (
      <>
        <li className="my-teams">
          {((!model.isSearchToggled && expanded) ||
            !expanded) && <span>My Libraries</span>}
          {model.isSearchToggled && expanded && (
            <div className="team-search">
              <SearchTextInputControlled
                model={model.teamSearchInput}
                persistentFeedback={false}
                id="search"
                name="search"
                type="text"
                autoComplete="off"
                placeholder="Search"
                showFeedback={false}
                alwaysShowClear={true}
                autoFocus
                onClear={() => model.handleSearchInputClear()}
              />
            </div>
          )}
          <div className="my-teams-buttons">
            {!model?.isSearchToggled && (
              <Tooltip content="Search Your Library">
                <button
                  className="btn btn-icon text-opacity-1 my-teams-button search-button"
                  aria-label="Search Your Library"
                  onClick={model?.handleToggleSearch}
                >
                  <span className="icon">
                    <SearchSVGIcon />
                  </span>
                </button>
              </Tooltip>
            )}
            {showCreateTeamBtn && (
              <NavLink
                className="create-team-link"
                to={createTeamLink}
                data-guided-training-id="sidebar-create-library">
                <Tooltip content="Create A New Library">
                  <button
                    className="btn btn-icon text-opacity-1 my-teams-button"
                    aria-label="Create A New Library">
                    <span className="icon">
                      <SideBarPlusSVGIcon />
                    </span>
                  </button>
                </Tooltip>
              </NavLink>
            )}
          </div>
        </li>
        {!expanded && (
          <NavLink
            className="create-team-link-collapsed"
            to={Routes.createTeam()}
            data-guided-training-id="sidebar-create-library"
          >
            <div className="my-teams-button">
              <Tooltip content="Create A New Library">
                <button
                  className="btn btn-icon medium"
                  aria-label="Create A New Library">
                  <span className="icon">
                    <SideBarPlusSVGIcon />
                  </span>
                </button>
              </Tooltip>
            </div>
          </NavLink>
        )}
        <li className="sidebar-nav-item sidebar-teams">
          <div className="sidebar-nav-scrollable">
            <ul className="sidebar-nav-sub-list bpad1" ref={teamList}>
              {model.isTeamListLoading && (
                <li
                  key="_loading_"
                  className="sidebar-nav-sub-item loading-item"
                >
                  <span className="icon">
                    <DotSpinner dots={3} className="small white" />
                  </span>
                </li>
              )}

              {!model.isTeamListLoading &&
                model.teams.map((team) => (
                  <li
                    key={team.id}
                    className="sidebar-nav-sub-item"
                    data-team-id={team.id}
                  >
                    <NavLink
                      className={({ isActive }) => {
                        const isActiveCustom = isActive ||
                          (model.currentTeamId === team.id && model.isTeamAnalytics);
                        return `btn btn-core sidepanel-sidebar${isActiveCustom ? ' selected' : ''}`;
                      }}
                      aria-current="page"
                      aria-expanded={expanded}
                      to={Routes.teamDashboard(team.id)}
                    >
                      <TeamAvatar className="icon" model={team} />
                      <span className="text">{team.name}</span>
                    </NavLink>
                  </li>
                ))}
            </ul>
          </div>
        </li>
      </>
    );
  }
);
